import { Calendar, CalendarDayButton } from "../../components/ui/calendar";
import { cn } from "../../lib/utils";
import { ComponentProps, useEffect, useMemo } from "react";
import { useInitialVisitStore } from "../../stores/initialVisitStore";
import { DateTime } from "luxon";
import { useInitialVisitAvailability } from "../../hooks/useInitialVisitAvailability";
import {
  CalendarXIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Loader2
} from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";

export function CalendarSection() {
  const today = DateTime.now();

  const {
    date,
    setDate,
    setVisibleMonth,
    uniqueId,
    visibleMonth,
    setSelectedSlot,
    selectedProvider
  } = useInitialVisitStore();

  const { data, isLoading } = useInitialVisitAvailability({
    uniqueId: uniqueId,
    enabled: Boolean(uniqueId)
  });

  const availableDatesSet = useMemo(() => {
    const set = new Set<string>();
    const availability = (data as any)?.availability || {};
    // availability is shaped as: { [providerId]: { [yyyy-MM-dd]: Slot[] } }
    if (selectedProvider?.value && selectedProvider.value !== "all") {
      const datesMap = (availability as any)[selectedProvider.value] || {};
      Object.entries(datesMap as Record<string, any[]>).forEach(
        ([dateStr, slots]) => {
          if (Array.isArray(slots) && slots.length > 0) {
            set.add(dateStr);
          }
        }
      );
    } else {
      Object.values(availability).forEach((datesMap: any) => {
        Object.entries(datesMap as Record<string, any[]>).forEach(
          ([dateStr, slots]) => {
            if (Array.isArray(slots) && slots.length > 0) {
              set.add(dateStr);
            }
          }
        );
      });
    }
    return set;
  }, [data, selectedProvider]);

  const isDayWithoutAvailability = (day: Date) => {
    const dateStr = DateTime.fromJSDate(day).toFormat("yyyy-MM-dd");
    return !availableDatesSet.has(dateStr);
  };

  // Check if there are any available days in the visible month
  const hasAvailableDaysInMonth = useMemo(() => {
    if (!visibleMonth || availableDatesSet.size === 0) return false;

    const startOfVisibleMonth = visibleMonth.startOf("month").startOf("day");
    const endOfVisibleMonth = visibleMonth.endOf("month").startOf("day");

    const minSelectableDay = today.hasSame(visibleMonth, "month")
      ? today.startOf("day")
      : startOfVisibleMonth;

    const candidates = Array.from(availableDatesSet)
      .map((dateStr) => DateTime.fromFormat(String(dateStr), "yyyy-MM-dd"))
      .filter(
        (dt) =>
          dt.isValid &&
          dt.toMillis() >= minSelectableDay.toMillis() &&
          dt.toMillis() <= endOfVisibleMonth.toMillis() &&
          dt.hasSame(visibleMonth, "month")
      );

    return candidates.length > 0;
  }, [availableDatesSet, visibleMonth, today]);

  // After switching months and when availability loads, default the selected date
  // to the next closest available day in the visible month. If none, keep the
  // first day (already set in onMonthChange).
  useEffect(() => {
    if (!visibleMonth || availableDatesSet.size === 0) return;

    const startOfVisibleMonth = visibleMonth.startOf("month").startOf("day");
    const endOfVisibleMonth = visibleMonth.endOf("month").startOf("day");

    const minSelectableDay = today.hasSame(visibleMonth, "month")
      ? today.startOf("day")
      : startOfVisibleMonth;

    const candidates = Array.from(availableDatesSet)
      .map((dateStr) => DateTime.fromFormat(String(dateStr), "yyyy-MM-dd"))
      .filter(
        (dt) =>
          dt.isValid &&
          dt.toMillis() >= minSelectableDay.toMillis() &&
          dt.toMillis() <= endOfVisibleMonth.toMillis() &&
          dt.hasSame(visibleMonth, "month")
      )
      .sort((a, b) => a.toMillis() - b.toMillis());

    const earliestAvailable = candidates[0];

    // Determine if current selection is selectable within this visible month
    const currentSelectedStr = date.toFormat("yyyy-MM-dd");
    const currentIsAvailable = availableDatesSet.has(currentSelectedStr);
    const currentInVisibleMonth = date.hasSame(visibleMonth, "month");
    const currentOnOrAfterMin = date.toMillis() >= minSelectableDay.toMillis();
    const currentOnOrBeforeEnd =
      date.toMillis() <= endOfVisibleMonth.toMillis();
    const isCurrentSelectable =
      currentInVisibleMonth &&
      currentOnOrAfterMin &&
      currentOnOrBeforeEnd &&
      currentIsAvailable;

    // Only auto-set if the current selection is not selectable
    if (!isCurrentSelectable && earliestAvailable) {
      setDate(earliestAvailable.startOf("day"));
    }
    // If no availability in this month, do nothing and keep the first day
  }, [availableDatesSet, visibleMonth, today, date, setDate]);

  return (
    <div className="relative py-2 lg:px-4 h-96 lg:h-auto border-t border-b lg:border-l lg:border-r lg:border-t-0 lg:border-b-0 border-border flex items-start shrink-0">
      {isLoading && (
        <div className="absolute top-1/2 left-0 right-0 z-50 px-6 lg:px-7 flex flex-col items-center justify-center pointer-events-none">
          <Loader2 className="size-6 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">
            Loading live availability...
          </p>
        </div>
      )}
      {!isLoading && !hasAvailableDaysInMonth && (
        <div className="absolute top-[6.5rem] md:top-[7rem] left-0 right-0 z-50 px-6 lg:px-7 flex items-start justify-center pointer-events-none">
          <Card className="bg-accent/50 w-full max-w-sm pointer-events-auto">
            <CardContent className="py-6 px-4">
              <div className="flex flex-col items-center justify-center gap-3 text-center">
                <div className="flex items-center justify-center size-12 rounded-full bg-accent">
                  <CalendarXIcon
                    className="size-6 stroke-[1.75] text-destructive"
                    aria-hidden="true"
                  />
                </div>
                <div className="max-w-xs">
                  <p className="text-base font-semibold">
                    No available times this month
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Select another month to continue booking.
                  </p>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground"
                    onClick={() => {
                      const prevMonth = visibleMonth.minus({ months: 1 });
                      setVisibleMonth(prevMonth);
                      const startOfMonth = prevMonth.startOf("day");
                      if (today.hasSame(startOfMonth, "month")) {
                        setDate(today.startOf("day"));
                      } else {
                        setDate(startOfMonth);
                      }
                    }}
                    aria-label="Go to previous month"
                  >
                    <ChevronLeftIcon className="mr-1" /> Previous
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => {
                      const nextMonth = visibleMonth.plus({ months: 1 });
                      const maxMonth = today.plus({ months: 6 });
                      if (nextMonth <= maxMonth) {
                        setVisibleMonth(nextMonth);
                        const startOfMonth = nextMonth.startOf("day");
                        if (today.hasSame(startOfMonth, "month")) {
                          setDate(today.startOf("day"));
                        } else {
                          setDate(startOfMonth);
                        }
                      }
                    }}
                    aria-label="Go to next month"
                  >
                    Next <ChevronRightIcon className="ml-1" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      <Calendar
        mode="single"
        selected={date.toJSDate()}
        onSelect={(newDate) => {
          if (newDate) {
            setDate(DateTime.fromJSDate(newDate));
            setSelectedSlot(null);
          }
        }}
        month={visibleMonth.toJSDate()}
        onMonthChange={(newMonth) => {
          if (newMonth) {
            setVisibleMonth(DateTime.fromJSDate(newMonth));
            const startOfMonth = DateTime.fromJSDate(newMonth).startOf("day");
            if (today.hasSame(startOfMonth, "month")) {
              setDate(today.startOf("day"));
            } else {
              setDate(startOfMonth);
            }
          }
        }}
        classNames={{
          today: "rounded-md"
        }}
        components={{
          CaptionLabel: ({ children, className, ...props }: any) => {
            const label = typeof children === "string" ? children : "";
            const [monthLabel, yearLabel] = label.split(" ");

            return (
              <div
                className={cn("flex items-baseline gap-1", className)}
                {...props}
              >
                <span className="text-lg font-semibold text-foreground">
                  {monthLabel || children}
                </span>
                {yearLabel && (
                  <span className="text-lg font-normal text-muted-foreground">
                    {yearLabel}
                  </span>
                )}
              </div>
            );
          },
          DayButton:
            isLoading || !hasAvailableDaysInMonth
              ? DayButtonSkeleton
              : DayButtonWithAccent
        }}
        disabled={[{ before: today.toJSDate() }, isDayWithoutAvailability]}
        modifiers={{
          booked: isDayWithoutAvailability
        }}
        modifiersClassNames={{
          booked: "[&>button]:line-through opacity-100"
        }}
        showOutsideDays={false}
        startMonth={today.toJSDate()}
        endMonth={new Date(today.year, today.month + 6, today.day)}
        className="[--cell-size:2.75rem] md:[--cell-size:3rem]"
        buttonVariant="ghost"
      />
    </div>
  );
}

function DayButtonSkeleton() {
  return (
    <div className="shrink-0 size-[2.75rem] md:size-[3rem] bg-background" />
  );
}

function DayButtonWithAccent(
  props: ComponentProps<typeof CalendarDayButton>
): JSX.Element {
  const { className, modifiers } = props;
  const isEnabled = !modifiers.disabled;
  const isToday = !!modifiers.today;
  const isInSelectedRange =
    !!modifiers.selected ||
    !!modifiers.range_start ||
    !!modifiers.range_end ||
    !!modifiers.range_middle;
  const isSelectedSingle =
    !!modifiers.selected &&
    !modifiers.range_start &&
    !modifiers.range_end &&
    !modifiers.range_middle;

  const todayDotBase =
    "relative after:content-[''] after:absolute after:left-1/2 after:-translate-x-1/2 after:bottom-1.5 after:w-1.5 after:h-1.5 after:rounded-full";

  const enhancedClassName = cn(
    className,
    // Accent background for enabled, non-today, non-selected days
    isEnabled &&
      !isToday &&
      !isInSelectedRange &&
      "bg-accent hover:ring-[1.5px] hover:ring-ring",
    // Dot indicator for today; white when selected, primary otherwise
    isToday &&
      (isSelectedSingle
        ? `${todayDotBase} after:bg-white`
        : `${todayDotBase} after:bg-primary`),
    "bg-accent hover:ring-[1.5px] hover:ring-ring"
  );

  return <CalendarDayButton {...props} className={enhancedClassName} />;
}
